import { getTranslations } from "next-intl/server"
import type { Metadata } from "next"

export const DEFAULT_OG_IMAGE_URL = "https://static.metal.com/common.metal.com/images/header-en/smm-logo.png"

/**
 * 解析 keywords 字符串为数组
 */
export function parseKeywords(input?: string): string[] {
  return (input || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
}

/**
 * TDK 占位符参数类型
 */
export interface TDKParams {
  /** title 占位符参数 */
  titleParams?: Record<string, string | number>
  /** description 占位符参数 */
  descriptionParams?: Record<string, string | number>
  /** keywords 占位符参数 */
  keywordsParams?: Record<string, string | number>
}

/**
 * 从国际化文件中获取 TDK 配置并生成 Metadata
 * @param locale 语言代码
 * @param namespace 国际化命名空间（如 "IndexPage", "AnnouncementsPage" 等）
 * @param params 可选的占位符参数，用于替换 TDK 中的动态内容
 * @returns Next.js Metadata 对象
 */
export async function getTDKFromTranslations(locale: string, namespace: string, params?: TDKParams): Promise<Metadata> {
  const t = await getTranslations({ locale, namespace })

  try {
    // 如果有占位符参数，使用 t() 进行插值；否则使用 t.raw() 获取原始字符串
    const title = params?.titleParams ? t("tdk.title", params.titleParams) : (t.raw("tdk.title") as string)
    const description = params?.descriptionParams
      ? t("tdk.description", params.descriptionParams)
      : (t.raw("tdk.description") as string)
    const keywordsStr = params?.keywordsParams
      ? t("tdk.keywords", params.keywordsParams)
      : (t.raw("tdk.keywords") as string)
    const keywords = parseKeywords(keywordsStr)

    return {
      title,
      description,
      keywords,
      // 社交媒体分享预览：当链接被分享时，平台会读取这些元数据，生成包含标题、描述和图片的卡片式预览。
      openGraph: {
        title,
        description,
        images: [DEFAULT_OG_IMAGE_URL],
        type: "website",
      },
    }
  } catch (error) {
    // 如果 TDK 配置不存在，尝试使用 IndexPage 的 TDK 作为默认值
    console.warn(`TDK not found for namespace: ${namespace}, falling back to IndexPage`, error)

    // 如果当前命名空间就是 IndexPage，直接返回硬编码默认值
    if (namespace === "IndexPage") {
      return {
        title: "Shanghai Metals Market (SMM)",
        description: "Metal prices, market data, and industry insights from Shanghai Metals Market (SMM).",
        keywords: ["metal prices", "SMM", "Shanghai Metals Market"],
        openGraph: {
          title: "Shanghai Metals Market (SMM)",
          description: "Metal prices, market data, and industry insights from Shanghai Metals Market (SMM).",
          images: [DEFAULT_OG_IMAGE_URL],
          type: "website",
        },
      }
    }

    // 尝试使用 IndexPage 的 TDK
    try {
      return await getTDKFromTranslations(locale, "IndexPage")
    } catch {
      // 如果 IndexPage 也不存在，返回硬编码默认值
      return {
        title: "Shanghai Metals Market (SMM)",
        description: "Metal prices, market data, and industry insights from Shanghai Metals Market (SMM).",
        keywords: ["metal prices", "SMM", "Shanghai Metals Market"],
        openGraph: {
          title: "Shanghai Metals Market (SMM)",
          description: "Metal prices, market data, and industry insights from Shanghai Metals Market (SMM).",
          images: [DEFAULT_OG_IMAGE_URL],
          type: "website",
        },
      }
    }
  }
}
